import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/config/supabase'
import { 
    Heart, Search, Filter, Plus, TrendingUp, Users, Calendar, 
    ArrowRight, Loader2, Award, History
} from 'lucide-react'
import { formatDate } from '@/lib/formatters'
import { useAuthStore } from '@/stores/auth.store'

export function FidelidadePage() {
    const [searchTerm, setSearchTerm] = useState('')
    const tenant = useAuthStore(s => s.tenant)

    const { data: stats, isLoading: statsLoading } = useQuery({
        queryKey: ['fidelidade_stats'],
        queryFn: async () => {
            const { data: extratoRows } = await supabase.schema('veicular_system').from('veic_pontos_extrato').select('associado_id, pontos')
            const total = extratoRows?.reduce((acc, curr) => acc + curr.pontos, 0) || 0
            
            // Contar associados únicos
            const uniqueAssociados = new Set(extratoRows?.map(r => r.associado_id))
            const totalAssociados = uniqueAssociados.size

            return { total, totalAssociados: totalAssociados || 0 }
        }
    })

    const { data: extrato, isLoading: extratoLoading } = useQuery({
        queryKey: ['fidelidade_extrato_geral', searchTerm],
        queryFn: async () => {
            let query = supabase.schema('veicular_system')
                .from('veic_pontos_extrato')
                .select('*, associado:veic_associados(nome, cpf)')
                .order('created_at', { ascending: false })
                .limit(50)

            if (searchTerm) {
                // @ts-ignore - filtragem simples por nome do associado via join
                query = query.filter('associado.nome', 'ilike', `%${searchTerm}%`)
            }

            const { data, error } = await query
            if (error) throw error
            return data
        }
    })

    return (
        <div className="p-8 space-y-8 animate-fade-in pb-20">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-surface-900 tracking-tight">Programa Sempre Juntos</h1>
                    <p className="text-surface-500 mt-1">Gerencie a pontuação e engajamento dos associados.</p>
                </div>
            </header>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard 
                    icon={Award} label="Pontos em Circulação" 
                    value={stats?.total.toLocaleString() || '0'} color="primary" 
                />
                <StatCard 
                    icon={Users} label="Associados Engajados" 
                    value={stats?.totalAssociados.toString() || '0'} color="success" 
                />
                <StatCard 
                    icon={TrendingUp} label="Média por Associado" 
                    value={stats?.totalAssociados ? (stats.total / stats.totalAssociados).toFixed(1) : '0'} color="info" 
                />
            </div>

            <div className="bg-white rounded-3xl shadow-elevated overflow-hidden border border-surface-100">
                <div className="p-6 border-b border-surface-100 flex flex-wrap gap-4 items-center justify-between bg-surface-50/30">
                    <div className="flex items-center gap-3 bg-white px-4 py-2.5 rounded-2xl border border-surface-200 w-full max-w-md shadow-sm">
                        <Search size={18} className="text-surface-400" />
                        <input 
                            type="text" placeholder="Buscar por associado..." 
                            className="bg-transparent border-none outline-none text-sm w-full"
                            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                         <button className="btn border border-surface-200 bg-white text-surface-700 gap-2">
                            <Filter size={16} /> Filtros
                         </button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-surface-50/50 border-b border-surface-100">
                                <th className="px-6 py-4 text-[10px] font-bold text-surface-700/40 uppercase tracking-widest">Data</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-surface-700/40 uppercase tracking-widest">Associado</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-surface-700/40 uppercase tracking-widest">Descrição</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-surface-700/40 uppercase tracking-widest text-right">Pontos</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-surface-50">
                            {extratoLoading ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center">
                                        <Loader2 className="animate-spin mx-auto text-primary-500" />
                                    </td>
                                </tr>
                            ) : extrato?.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center text-surface-400">Nenhuma movimentação encontrada.</td>
                                </tr>
                            ) : (
                                extrato?.map(item => (
                                    <tr key={item.id} className="hover:bg-surface-50/50 transition-colors group">
                                        <td className="px-6 py-4 text-sm text-surface-500">{formatDate(item.created_at)}</td>
                                        <td className="px-6 py-4">
                                            <p className="font-semibold text-surface-900 leading-none">{(item.associado as any)?.nome}</p>
                                            <p className="text-xs text-surface-400 mt-1">{(item.associado as any)?.cpf}</p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <span className={`w-2 h-2 rounded-full ${getTipoColor(item.tipo)}`} />
                                                <div>
                                                    <p className="text-sm font-medium text-surface-700">{item.descricao}</p>
                                                    <p className="text-[10px] font-extrabold uppercase text-surface-400 tracking-tighter">{item.tipo}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className={`px-6 py-4 text-right font-bold text-lg ${item.pontos > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {item.pontos > 0 ? '+' : ''}{item.pontos}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

function StatCard({ icon: Icon, label, value, color }: any) {
    const colors: any = {
        primary: 'bg-primary-50 text-primary-600',
        success: 'bg-green-50 text-green-600',
        info: 'bg-blue-50 text-blue-600',
    }

    return (
        <div className="bg-white p-6 rounded-3xl border border-surface-100 shadow-sm flex items-center gap-5">
            <div className={`p-4 rounded-2xl ${colors[color]}`}>
                <Icon size={24} />
            </div>
            <div>
                <p className="text-xs font-bold text-surface-400 uppercase tracking-widest">{label}</p>
                <p className="text-2xl font-bold text-surface-900">{value}</p>
            </div>
        </div>
    )
}

function getTipoColor(tipo: string) {
    switch (tipo) {
        case 'checkin': return 'bg-primary-500'
        case 'contratacao': return 'bg-green-500'
        case 'manual': return 'bg-blue-500'
        case 'resgate': return 'bg-red-500'
        default: return 'bg-surface-300'
    }
}
